import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Progress,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import classnames from 'classnames';
import moment from 'moment';
import Header from 'components/Headers/Header.js';
import { axiosGet, axiosPost, axiosPut, axiosDelete } from 'util/apiConfig.js';
import { accountLabel } from 'util/constants';
import { FaHistory, FaPaperPlane, FaUserCheck, FaInfoCircle, FaFileAlt, FaPen, FaColumns, FaKey, FaShieldAlt, FaRocket, FaKeyboard, FaList, FaSave, FaEdit, FaTrashAlt, FaBookmark, FaPlay, FaCheck } from 'react-icons/fa';

// Robust phone formatting utility matching the backend implementation
const formatPhoneNumber = (input) => {
  if (!input || typeof input !== "string") {
    return 'N/A';
  }
  
  let str = input.trim();
  
  // Check if it has a whatsapp prefix, remove it
  str = str.replace(/^whatsapp:/i, '');
  
  // Strip leading zeros
  str = str.replace(/^0+/, '');
  
  // Retain only digits and '+'
  str = str.replace(/[^\d+]/g, '');
  
  // Normalize nested prefixes (e.g. +1+1, +91+91, +91+1, etc.)
  str = str.replace(/^\+(?:91\+)+/g, '+91');
  str = str.replace(/^\+(?:1\+)+/g, '+1');
  str = str.replace(/^\++/, '+');
  
  // Extract only digits
  let digits = str.replace(/\D/g, '');
  
  // 1. Detect if the number belongs to India (+91 or starts with 91)
  const isIndia = str.startsWith('+91') || (digits.startsWith('91') && !str.startsWith('+1') && digits.length >= 12);
  
  if (isIndia) {
    // Clean up duplicate country codes, e.g. 91919876543210 (14 digits)
    if (digits.startsWith('9191') && digits.length === 14) {
      digits = digits.substring(2);
    }
    
    // If it starts with 91 and has 12 digits (91 + 10-digit number)
    if (digits.startsWith('91') && digits.length === 12) {
      return `+${digits}`;
    }
    
    // If it has only 10 digits, prepend +91
    if (digits.length === 10) {
      return `+91${digits}`;
    }
    
    // General Indian number format range (10 to 13 digits)
    if (digits.length >= 10 && digits.length <= 13) {
      if (!digits.startsWith('91')) {
        return `+91${digits}`;
      }
      return `+${digits}`;
    }
  }
  
  // 2. Treat as US (+1) number
  // Clean up duplicate country codes, e.g. 1114086146768 (13 digits) or 114086146768 (12 digits)
  if (digits.startsWith('111') && digits.length === 13) {
    digits = digits.substring(2);
  } else if (digits.startsWith('11') && digits.length === 12) {
    digits = digits.substring(1);
  }
  
  // If it has 10 digits, prepend +1
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  
  // If it has 11 digits and starts with 1, prepend +
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  
  // Support non-prepended international phone numbers (7 to 15 digits)
  if (digits.length >= 7 && digits.length <= 15) {
    return `+${digits}`;
  }
  
  return input; // Fallback to raw if invalid
};

const BulkMessage = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [viewMode, setViewMode] = useState('focus'); // 'focus' or 'split'
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [hideMessageDetails, setHideMessageDetails] = useState(true);
  const [explicitCustomKeys, setExplicitCustomKeys] = useState({});
  const [sendingMessages, setSendingMessages] = useState(false);

  // Global Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert', // 'alert' | 'confirm'
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Draft campaigns state
  const [drafts, setDrafts] = useState([]);

  const closeDialog = () => setConfirmDialog(prev => ({ ...prev, isOpen: false }));

  const showAlert = (title, message) => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      type: 'alert',
      onConfirm: closeDialog,
      onCancel: null,
      confirmText: 'OK',
      cancelText: ''
    });
  };

  const showConfirm = (title, message, onConfirm, confirmText = 'Confirm') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      type: 'confirm',
      onConfirm: () => {
        closeDialog();
        onConfirm();
      },
      onCancel: closeDialog,
      confirmText,
      cancelText: 'Cancel'
    });
  };

  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [editingDraftId, setEditingDraftId] = useState(null); // null = new draft, string = updating existing
  const [allRecipients, setAllRecipients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaignRecipients, setLoadingCampaignRecipients] = useState({});
  const [campaignLogPages, setCampaignLogPages] = useState({});
  const logItemsPerPage = 50;

  const fetchCampaignRecipients = async (campaignId) => {
    const existing = campaigns.find(c => c.campaignId === campaignId);
    if (existing && existing.recipients && existing.recipients.length > 0) {
      return existing.recipients;
    }
    setLoadingCampaignRecipients(prev => ({ ...prev, [campaignId]: true }));
    try {
      const res = await axiosGet(`message/bulk/campaigns/${campaignId}`);
      if (!res.isError && res.data) {
        setCampaigns(prev => prev.map(c => c.campaignId === campaignId ? { ...c, recipients: res.data.recipients } : c));
        return res.data.recipients;
      }
    } catch (e) {
      console.error('Error fetching campaign recipients:', e);
    } finally {
      setLoadingCampaignRecipients(prev => ({ ...prev, [campaignId]: false }));
    }
    return [];
  };
  const [classesList, setClassesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const [showTemplateMapping, setShowTemplateMapping] = useState(false);
  const [mappingTab, setMappingTab] = useState('active'); // active, expired, sales_lead
  const [classKeyMap, setClassKeyMap] = useState(() => {
    const saved = localStorage.getItem('fiverkids_bulk_class_map');
    return saved ? JSON.parse(saved) : {};
  });

  const [templateKeyMap, setTemplateKeyMap] = useState(() => {
    const saved = localStorage.getItem('fiverkids_bulk_template_key_map_v2');
    if (saved) return JSON.parse(saved);

    return {
      active: {
        studentName: 'studentName',
        sectionName: 'sectionName',
        classKey: 'classKey',
        zoomLink: 'zoomLink',
        startDate: 'startDate',
        teacherName: 'teacherName',
        mobileNumber: 'phone'
      },
      expired: {
        studentName: 'studentName',
        sectionName: 'sectionName',
        classKey: 'classKey',
        zoomLink: 'zoomLink',
        startDate: 'startDate',
        teacherName: 'teacherName',
        mobileNumber: 'phone'
      },
      sales_lead: {
        studentName: 'name',
        sectionName: 'sectionName',
        classKey: 'classKey',
        zoomLink: 'zoomLink',
        startDate: 'startTime',
        teacherName: 'createdBy',
        mobileNumber: 'mobile'
      }
    };
  });

  // Filter States
  const [simulate, setSimulate] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState({
    Coding: true,
    Chess: true,
    Maths: true,
    SpeakClub: true,
  });
  const [selectedStatuses, setSelectedStatuses] = useState({
    active: true,
    expired: true,
    sales: true,
  });

  // Template State
  const [messageTemplate, setMessageTemplate] = useState(
    'Hi {studentName},\n\nWe are excited to offer a summer camp for {classKey}!'
  );

  // Selection state for recipients
  const [selectedRecipients, setSelectedRecipients] = useState({});
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Sending progress
  const [currentCampaignId, setCurrentCampaignId] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState({
    total: 0,
    success: 0,
    failed: 0,
    active: false
  });

  // Derived filtered recipients list with unique phone numbers
  const activeClasses = Object.keys(selectedClasses).filter((k) => selectedClasses[k]);
  const getFilteredRecipients = () => {
    const seenPhones = new Set();
    const result = [];
    allRecipients.forEach((item) => {
      const classKey = classKeyMap[item.classId] || 'Coding';
      // 1. Subscription status filter
      const matchesStatus =
        (selectedStatuses.active && item.stripeSubscriptionStatus === 'active') ||
        (selectedStatuses.expired && item.stripeSubscriptionStatus === 'expired') ||
        (selectedStatuses.sales && item.stripeSubscriptionStatus === 'sales_lead');

      // 2. Class filter
      const matchesClass = activeClasses.includes(classKey);

      if (matchesStatus && matchesClass) {
        const rawPhone = item.phone || '';
        const formattedPhone = formatPhoneNumber(rawPhone);
        if (formattedPhone && formattedPhone !== 'N/A') {
          if (!seenPhones.has(formattedPhone)) {
            seenPhones.add(formattedPhone);
            result.push(item);
          }
        } else {
          result.push(item);
        }
      }
    });
    return result;
  };
  const filteredRecipients = getFilteredRecipients();

  // Derived list after applying the Name or Phone search filter
  const searchedRecipients = filteredRecipients.filter(r => {
    if (!recipientSearchQuery) return true;
    const query = recipientSearchQuery.toLowerCase().trim();
    const nameMatch = r.studentName && r.studentName.toLowerCase().includes(query);
    const phoneMatch = r.phone && r.phone.toLowerCase().includes(query);
    const emailMatch = r.email && r.email.toLowerCase().includes(query);
    return nameMatch || phoneMatch || emailMatch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchedRecipients.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(searchedRecipients.length / itemsPerPage);


  // Fetch recipients on filter change (fetches active and inactive at once)
  const fetchRecipients = async (isInitial = false) => {
    setLoadingRecipients(true);
    try {
      let currentClasses = classesList;
      if (classesList.length === 0) {
        const res = await axiosGet('class/get-classes-list');
        if (!res.isError && res.data) {
          setClassesList(res.data);
          currentClasses = res.data;
        }
      }

      const promises = [
        axiosGet('section/get-private-sections/1/1000000?sectionType=2&status=active'),
        axiosGet('section/get-private-sections/1/1000000?sectionType=2&status=inactive'),
        axiosGet('sales/get-all-sales-notification/1/1000000')
      ];

      const responses = await Promise.all(promises);
      const activeRes = responses[0];
      const inactiveRes = responses[1];
      const salesRes = responses[2];

      const combinedResults = [];
      if (!activeRes.isError && activeRes.data && activeRes.data.result) {
        combinedResults.push(...activeRes.data.result);
      }
      if (!inactiveRes.isError && inactiveRes.data && inactiveRes.data.result) {
        combinedResults.push(...inactiveRes.data.result);
      }

      const uniqueEmails = [...new Set(combinedResults.map((item) => item.email).filter(Boolean))];
      let profileMap = {};
      if (uniqueEmails.length > 0) {
        const resolveRes = await axiosPost('message/bulk/resolve-recipients', { emails: uniqueEmails });
        if (!resolveRes.isError && resolveRes.data) {
          profileMap = resolveRes.data;
        }
      }

      const uniqueMap = {};
      const uniqueList = [];

      const classMap = {};
      currentClasses.forEach((c) => {
        classMap[c.classId] = c.name;
      });

      // 1. Map Private Section Users
      combinedResults.forEach((item) => {
        const key = `${item.email}_${item.sectionId}`;
        if (!uniqueMap[key]) {
          uniqueMap[key] = true;

          const profile = profileMap[item.email.toLowerCase().trim()] || {};
          const studentName = profile.name || item.email.split('@')[0];
          const phone = profile.phone || item.mobileNumber || '';

          uniqueList.push({
            ...item,
            email: item.email,
            phone: phone,
            studentName: studentName,
            stripeSubscriptionStatus: item.stripeSubscriptionStatus === 'active' || item.stripeSubscriptionStatus === 'trialing' || item.stripeSubscriptionStatus === 'unpaid' ? 'active' : 'expired',
            sectionName: item.sectionName,
            sectionId: item.sectionId,
            classId: item.classId,
            zoomLink: item.zoomLink || '',
            startDate: item.startDate || '',
            teacherName: item.teacherName || '',
          });
        }
      });

      // 2. Map Sales Notifications
      if (!salesRes.isError && salesRes.data && salesRes.data.result) {
        salesRes.data.result.forEach((item) => {
          if (!item.email) return;
          const key = `${item.email}_${item.notificationId}`;
          if (!uniqueMap[key]) {
            uniqueMap[key] = true;

            uniqueList.push({
              ...item,
              email: item.email,
              phone: item.mobile || '',
              studentName: item.name || item.email.split('@')[0],
              stripeSubscriptionStatus: 'sales_lead',
              sectionName: 'Sales Notification / Lead',
              sectionId: item.notificationId,
              classId: item.classId,
              zoomLink: item.zoomLink || '',
              startDate: item.startTime || '',
              teacherName: item.createdBy || '',
            });
          }
        });
      }

      setAllRecipients(uniqueList);
      setCurrentPage(1);

      // Default all items to unchecked
      setSelectedRecipients({});
      if (!isInitial) {
        setViewMode('split');
      }
    } catch (e) {
      console.error('Error fetching recipients:', e);
    } finally {
      setLoadingRecipients(false);
      setHasFetched(true);
    }
  };

  // Fetch campaigns history
  const fetchCampaigns = async () => {
    try {
      const res = await axiosGet('message/bulk/campaigns');
      if (!res.isError && res.data) {
        setCampaigns(res.data);
      }
    } catch (e) {
      console.error('Error fetching campaigns:', e);
    }
  };

  // Fetch classes on mount to map classId to classKey
  const fetchClasses = async () => {
    try {
      const res = await axiosGet('class/get-classes-list');
      if (!res.isError && res.data) {
        setClassesList(res.data);
      }
    } catch (e) {
      console.error('Error fetching classes:', e);
    }
  };

  // Run on mount - load all campaigns, classes, and recipients immediately
  useEffect(() => {
    fetchClasses();
    fetchCampaigns();
    fetchRecipients(true); // pass true to preserve focused filter view initially
  }, []);

  // Automatically initialize defaults on class list update
  useEffect(() => {
    if (classesList.length > 0) {
      setClassKeyMap((prev) => {
        const updated = { ...prev };
        let changed = false;
        classesList.forEach((c) => {
          if (!updated[c.classId]) {
            let detected = 'Coding';
            const nameLower = (c.name || '').toLowerCase();
            if (nameLower.includes('chess')) detected = 'Chess';
            else if (nameLower.includes('math')) detected = 'Maths';
            else if (c.classId === 'CLASS1784904166957' || nameLower.includes('speak') || nameLower.includes('public speaking')) detected = 'SpeakClub';
            updated[c.classId] = detected;
            changed = true;
          }
        });
        if (changed) {
          localStorage.setItem('fiverkids_bulk_class_map', JSON.stringify(updated));
        }
        return updated;
      });
    }
  }, [classesList]);

  const handleMapKeyChange = (classId, newKey) => {
    setClassKeyMap((prev) => {
      const updated = {
        ...prev,
        [classId]: newKey
      };
      localStorage.setItem('fiverkids_bulk_class_map', JSON.stringify(updated));
      return updated;
    });
    setCurrentPage(1);
  };

  const handleTemplateKeyMapChange = (stateKey, variable, apiKey) => {
    setTemplateKeyMap((prev) => {
      const updated = {
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          [variable]: apiKey
        }
      };
      localStorage.setItem('fiverkids_bulk_template_key_map_v2', JSON.stringify(updated));
      return updated;
    });
  };

  // ── Draft Campaign Handlers ──────────────────────────────────────────────────

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const res = await axiosGet('message/bulk/drafts');
      if (!res.isError && res.data) setDrafts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDrafts(false); }
  };

  const handleSaveDraft = async () => {
    if (!draftName.trim()) return;
    setSavingDraft(true);
    try {
      const payload = {
        name: draftName.trim(),
        messageTemplate,
        classFilters: Object.keys(selectedClasses).filter(k => selectedClasses[k]),
        subscriptionFilters: Object.keys(selectedStatuses).filter(k => selectedStatuses[k]),
        templateKeyMap,
        classKeyMap,
        simulate,
        recipients: allRecipients,  // snapshot the full resolved recipient list
      };
      if (editingDraftId) {
        const res = await axiosPut(`message/bulk/drafts/${editingDraftId}`, payload);
        if (!res.isError) {
          setDrafts(prev => prev.map(d => d.draftId === editingDraftId ? res.data : d));
          showAlert('Success', 'Campaign updated!');
        }
      } else {
        const res = await axiosPost('message/bulk/drafts', payload);
        if (!res.isError) {
          setDrafts(prev => [res.data, ...prev]);
          showAlert('Success', 'Campaign saved!');
        }
      }
      setShowSaveModal(false);
      setDraftName('');
      setEditingDraftId(null);
    } catch (e) { console.error(e); showAlert('Error', 'Failed to save campaign.'); }
    finally { setSavingDraft(false); }
  };

  const handleDeleteDraft = (draftId) => {
    showConfirm('Delete Draft', 'Delete this saved campaign?', async () => {
      try {
        await axiosDelete(`message/bulk/drafts/${draftId}`);
        setDrafts(prev => prev.filter(d => d.draftId !== draftId));
      } catch (e) { console.error(e); }
    }, 'Delete');
  };

  const handleLoadDraft = (draft) => {
    setMessageTemplate(draft.messageTemplate || '');
    if (draft.classFilters) {
      setSelectedClasses({ Coding: false, Chess: false, Maths: false, SpeakClub: false, ...Object.fromEntries(draft.classFilters.map(k => [k, true])) });
    }
    if (draft.subscriptionFilters) {
      setSelectedStatuses({ active: false, expired: false, sales: false, ...Object.fromEntries(draft.subscriptionFilters.map(k => [k, true])) });
    }
    if (draft.templateKeyMap) setTemplateKeyMap(draft.templateKeyMap);
    if (draft.classKeyMap) setClassKeyMap(draft.classKeyMap);
    if (draft.simulate !== undefined) setSimulate(draft.simulate);
    // Restore the snapshotted recipients so the table is pre-populated
    if (draft.recipients && draft.recipients.length > 0) {
      setAllRecipients(draft.recipients);
      setHasFetched(true);
    } else {
      setAllRecipients([]);
      setHasFetched(false);
    }
    setSelectedRecipients({});
    setCurrentPage(1);
    setActiveTab('send');
    setViewMode('split');
    setEditingDraftId(draft.draftId);
    setDraftName(draft.name);
  };

  const handleSendLiveFromSimulation = async (campaign) => {
    let campaignRecipients = campaign.recipients;
    if (!campaignRecipients || campaignRecipients.length === 0) {
      setSendingMessages(true);
      campaignRecipients = await fetchCampaignRecipients(campaign.campaignId);
      setSendingMessages(false);
    }

    if (!campaignRecipients || campaignRecipients.length === 0) {
      showAlert('Error', 'Failed to retrieve recipient details for this campaign. Please try again.');
      return;
    }

    showConfirm(
      'Send Live Now',
      `Are you sure you want to promote this simulated campaign to Live? This will immediately begin dispatching real messages to the ${campaignRecipients.length} recipients. This cannot be undone.`,
      async () => {
        setSendingMessages(true);
        try {
          const payload = {
            recipients: campaignRecipients,
            messageTemplate: campaign.messageTemplate,
            classFilters: campaign.classFilters,
            subscriptionFilters: campaign.subscriptionFilters,
            simulate: false,
            promotedFrom: campaign.campaignId,
          };

          const res = await axiosPost('message/bulk/send', payload);
          if (!res.isError && res.data) {
            const liveCampaign = res.data;
            setCurrentCampaignId(liveCampaign.campaignId);
            setCampaignProgress({
              total: liveCampaign.totalCount,
              success: 0,
              failed: 0,
              active: true
            });
            showAlert('Success', 'Live campaign has been scheduled successfully!');
            fetchCampaigns();
          } else {
            showAlert('Error', res.message || 'Failed to dispatch live campaign.');
          }
        } catch (err) {
          console.error(err);
          showAlert('Error', 'Error promoting campaign to live.');
        } finally {
          setSendingMessages(false);
        }
      },
      'Send Live'
    );
  };


  const [editingRowKey, setEditingRowKey] = useState(null); // stores `${r.email}_${r.sectionId}`
  const [editForm, setEditForm] = useState({ studentName: '', phone: '', zoomLink: '', startDate: '', teacherName: '' });

  const startEditingRow = (r) => {
    const itemKey = `${r.email}_${r.sectionId}`;
    setEditingRowKey(itemKey);
    setEditForm({
      studentName: r.studentName || '',
      phone: r.phone || '',
      zoomLink: r.zoomLink || '',
      startDate: r.startDate || '',
      teacherName: r.teacherName || ''
    });
  };

  const saveRowEdit = (itemKey) => {
    setAllRecipients((prev) =>
      prev.map((r) => {
        const key = `${r.email}_${r.sectionId}`;
        if (key === itemKey) {
          return {
            ...r,
            studentName: editForm.studentName,
            phone: editForm.phone,
            zoomLink: editForm.zoomLink,
            startDate: editForm.startDate,
            teacherName: editForm.teacherName
          };
        }
        return r;
      })
    );
    setEditingRowKey(null);
  };

  const cancelRowEdit = () => {
    setEditingRowKey(null);
  };

  // Poll progress if a campaign is active
  useEffect(() => {
    let intervalId;
    if (campaignProgress.active && currentCampaignId) {
      intervalId = setInterval(async () => {
        try {
          const res = await axiosGet('message/bulk/campaigns');
          if (!res.isError && res.data) {
            setCampaigns(res.data);
            const current = res.data.find((c) => c.campaignId === currentCampaignId);
            if (current) {
              const totalProcessed = current.successCount + current.failedCount;
              setCampaignProgress({
                total: current.totalCount,
                success: current.successCount,
                failed: current.failedCount,
                active: totalProcessed < current.totalCount
              });
              if (totalProcessed >= current.totalCount) {
                setCurrentCampaignId(null);
                setSendingMessages(false);
              }
            }
          }
        } catch (e) {
          console.error(e);
        }
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [campaignProgress.active, currentCampaignId]);

  // Handle Class toggling
  const handleClassToggle = (key) => {
    setSelectedClasses((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setCurrentPage(1);
  };

  // Handle Status toggling
  const handleStatusToggle = (key) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setCurrentPage(1);
  };

  // Handle template variable insertion
  const insertVariable = (placeholder) => {
    setMessageTemplate((prev) => prev + placeholder);
  };

  // Interpolation logic for previewing custom message
  const interpolateMessage = (template, r) => {
    if (!template) return '';

    // Resolve subscription status key ('active', 'expired', or 'sales_lead')
    const stateKey = r.stripeSubscriptionStatus || 'active';
    const statusMap = templateKeyMap[stateKey] || templateKeyMap['active'] || {};

    // Dynamically find all unique placeholder variables in the template (e.g. {studentName}, {apple})
    const placeholders = [...new Set([...template.matchAll(/{([^}]+)}/g)].map(m => m[1]))];

    let result = template;
    placeholders.forEach((placeholder) => {
      // Map to the configured API response key, fallback to placeholder name itself
      const mappedKey = statusMap[placeholder] !== undefined ? statusMap[placeholder] : placeholder;
      
      const getValue = () => {
        if (mappedKey === 'classKey') {
          return accountLabel(classKeyMap[r.classId] || 'Coding');
        }
        const val = r[mappedKey];
        if (mappedKey === 'startDate' && val) {
          return moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : val;
        }
        if (mappedKey === 'startTime' && val) {
          return moment(val).isValid() ? moment(val).format('YYYY-MM-DD HH:mm') : val;
        }
        return val !== undefined && val !== null ? String(val) : '';
      };

      // Escape regex special chars in placeholder just in case
      const escapedPlaceholder = placeholder.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      result = result.replace(new RegExp(`{${escapedPlaceholder}}`, 'g'), getValue());
    });

    return result;
  };


  // Toggle selection for current page recipients
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectedRecipients((prev) => {
      const newSelection = { ...prev };
      currentItems.forEach((r) => {
        const itemKey = `${r.email}_${r.sectionId}`;
        newSelection[itemKey] = isChecked;
      });
      return newSelection;
    });
  };

  // Toggle single selection
  const handleSelectRecipient = (itemKey) => {
    setSelectedRecipients((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // Submit/Send Campaign
  const handleSendCampaign = () => {
    const selectedList = filteredRecipients.filter((r) => {
      const itemKey = `${r.email}_${r.sectionId}`;
      return !!selectedRecipients[itemKey];
    });
    if (selectedList.length === 0) {
      showAlert('No Recipients', 'Please select at least one recipient.');
      return;
    }

    showConfirm(
      simulate ? 'Run Simulation' : 'Send Bulk Campaign',
      `Are you sure you want to ${simulate ? 'simulate' : 'send'} this bulk campaign to ${selectedList.length} users?`,
      async () => {
        setSendingMessages(true);
        try {
          const activeClasses = Object.keys(selectedClasses).filter((k) => selectedClasses[k]);
          const activeStatuses = Object.keys(selectedStatuses).filter((k) => selectedStatuses[k]);

          const payload = {
            recipients: selectedList.map((r) => ({
              ...r,
              classKey: classKeyMap[r.classId] || 'Coding',
              customMessage: interpolateMessage(messageTemplate, r),
            })),
            messageTemplate,
            classFilters: activeClasses,
            subscriptionFilters: activeStatuses,
            simulate: simulate,
          };

          const res = await axiosPost('message/bulk/send', payload);
          if (!res.isError && res.data) {
            const campaign = res.data;
            setCurrentCampaignId(campaign.campaignId);
            setCampaignProgress({
              total: campaign.totalCount,
              success: 0,
              failed: 0,
              active: true
            });
            fetchCampaigns();
          } else {
            setSendingMessages(false);
            showAlert('Error', res.message || 'Failed to trigger bulk send.');
          }
        } catch (e) {
          console.error(e);
          setSendingMessages(false);
          showAlert('Error', 'Something went wrong. Please try again.');
        }
      },
      simulate ? 'Simulate' : 'Send'
    );
  };

  return (
    <>
      <style>{`
        /* Dynamic premium styling and micro-interactions */
        .card-premium {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(15px) !important;
          border: 1px solid rgba(255, 255, 255, 0.8) !important;
          box-shadow: 0 12px 35px 0 rgba(31, 38, 135, 0.05) !important;
          border-radius: 16px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .card-premium:hover {
          box-shadow: 0 18px 45px 0 rgba(31, 38, 135, 0.08) !important;
        }
        .btn-pill-premium {
          border-radius: 20px !important;
          font-weight: 600 !important;
          text-transform: none !important;
          font-size: 11px !important;
          letter-spacing: 0.2px !important;
          padding: 4px 12px !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .btn-pill-premium:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
        }
        .custom-scroll::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: #f1f3f9;
          border-radius: 10px;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .animate-pulse-slow {
          animation: pulse 2.5s infinite ease-in-out;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(0.99); }
        }
        .text-gradient {
          background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Mobile responsive card layouts for tables */
        @media (max-width: 767.98px) {
          .table-responsive-mobile, 
          .table-responsive-mobile tbody, 
          .table-responsive-mobile tr, 
          .table-responsive-mobile td {
            display: block !important;
            width: 100% !important;
          }
          .table-responsive-mobile thead {
            display: none !important;
          }
          .table-responsive-mobile tr {
            margin-bottom: 1.25rem !important;
            background: #ffffff !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 14px !important;
            padding: 1.25rem !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02) !important;
            transition: all 0.2s ease !important;
            position: relative !important;
          }
          .table-responsive-mobile tr:hover {
            background: #f8fafc !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04) !important;
          }
          .table-responsive-mobile tr.no-cards {
            display: table-row !important;
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
          }
          .table-responsive-mobile tr.no-cards td {
            display: table-cell !important;
            border: none !important;
            text-align: center !important;
            background: transparent !important;
          }
          .table-responsive-mobile tr.no-cards td::before {
            display: none !important;
          }
          .table-responsive-mobile td {
            text-align: left !important;
            padding: 0.625rem 0 !important;
            border: none !important;
            border-bottom: 1px dashed #f1f5f9 !important;
            white-space: normal !important;
            min-height: auto !important;
          }
          .table-responsive-mobile td:last-child {
            border-bottom: none !important;
            padding-bottom: 0 !important;
          }
          .table-responsive-mobile td:first-child {
            padding-top: 0 !important;
          }
          .table-responsive-mobile td[data-label]::before {
            content: attr(data-label);
            display: block !important;
            font-size: 10px !important;
            font-weight: 800 !important;
            text-transform: uppercase !important;
            color: #64748b !important;
            margin-bottom: 0.35rem !important;
            letter-spacing: 0.5px !important;
          }
          .table-responsive-mobile td.checkbox-cell {
            display: flex !important;
            align-items: center !important;
            border-bottom: 1px solid #f1f5f9 !important;
            padding-bottom: 0.75rem !important;
          }
          .table-responsive-mobile td.checkbox-cell::before {
            content: attr(data-label);
            margin-bottom: 0 !important;
            margin-right: auto !important;
            font-size: 11px !important;
          }
          .table-responsive-mobile td.checkbox-cell input[type="checkbox"] {
            width: 20px !important;
            height: 20px !important;
            margin: 0 !important;
          }
          .table-responsive-mobile td.action-cell {
            padding-top: 0.75rem !important;
            display: flex !important;
            justify-content: flex-end !important;
          }
          .table-responsive-mobile td.action-cell::before {
            display: none !important;
          }
          .table-responsive-mobile td.action-cell button {
            width: 100% !important;
            padding: 10px !important;
            font-size: 12px !important;
          }
        }

        .mapping-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }
        @media (max-width: 575.98px) {
          .mapping-row {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .mapping-row label {
            margin-bottom: 0.35rem !important;
            padding-right: 0 !important;
          }
          .mapping-row select {
            width: 100% !important;
          }
        }
      `}</style>

      <Header />
      <Container className="mt--7" fluid>
        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <div className="p-1 bg-white shadow-sm border" style={{ borderRadius: '14px' }}>
                  <Nav pills className="nav-fill flex-column flex-md-row border-0">
                <NavItem className="p-1">
                  <NavLink
                    className={classnames('py-2 font-weight-bold align-items-center justify-content-center d-flex border-0', {
                      active: activeTab === 'send',
                    })}
                    onClick={() => setActiveTab('send')}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '10px',
                      fontSize: '12px',
                      transition: 'all 0.3s ease',
                      backgroundColor: activeTab === 'send' ? '#5e72e4' : 'transparent',
                      color: activeTab === 'send' ? '#ffffff' : '#525f7f',
                      boxShadow: activeTab === 'send' ? '0 4px 15px rgba(94, 114, 228, 0.25)' : 'none',
                    }}
                  >
                    Campaign Dispatcher Studio
                  </NavLink>
                </NavItem>
                <NavItem className="p-1">
                  <NavLink
                    className={classnames('py-2 font-weight-bold align-items-center justify-content-center d-flex border-0', {
                      active: activeTab === 'history',
                    })}
                    onClick={() => {
                      setActiveTab('history');
                      fetchCampaigns();
                    }}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      fontSize: '12px',
                      transition: 'all 0.3s ease',
                      backgroundColor: activeTab === 'history' ? '#5e72e4' : 'transparent',
                      color: activeTab === 'history' ? '#ffffff' : '#525f7f',
                      boxShadow: activeTab === 'history' ? '0 4px 15px rgba(94, 114, 228, 0.25)' : 'none',
                    }}
                  >
                    <FaHistory className="mr-2" size={13} />
                    Campaign Historical Logs
                  </NavLink>
                </NavItem>
                <NavItem className="p-1">
                  <NavLink
                    className={classnames('py-2 font-weight-bold align-items-center justify-content-center d-flex border-0', {
                      active: activeTab === 'drafts',
                    })}
                    onClick={() => {
                      setActiveTab('drafts');
                      fetchDrafts();
                    }}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      fontSize: '12px',
                      transition: 'all 0.3s ease',
                      backgroundColor: activeTab === 'drafts' ? '#2dce89' : 'transparent',
                      color: activeTab === 'drafts' ? '#ffffff' : '#525f7f',
                      boxShadow: activeTab === 'drafts' ? '0 4px 15px rgba(45,206,137,0.25)' : 'none',
                    }}
                  >
                    <FaBookmark className="mr-2" size={13} />
                    Saved Campaigns
                    {drafts.length > 0 && (
                      <Badge color="success" pill className="ml-2" style={{ fontSize: '9px' }}>{drafts.length}</Badge>
                    )}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </Col>
        </Row>


        <TabContent activeTab={activeTab}>
          {/* BULK DISPATCHER TAB */}
          <TabPane tabId="send">
            {/* View Mode Toggle Switch */}
            <Row className="mb-3">
              <Col className="d-flex justify-content-end align-items-center">
                <span className="text-muted mr-3 font-weight-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                  WORKSPACE VIEW:
                </span>
                <div className="p-1 bg-white shadow-sm border d-inline-flex" style={{ borderRadius: '10px' }}>
                  <Button
                    size="sm"
                    className="border-0 px-2 py-1 font-weight-bold mb-0"
                    onClick={() => setViewMode('focus')}
                    style={{
                      borderRadius: '6px',
                      fontSize: '11px',
                      textTransform: 'none',
                      backgroundColor: viewMode === 'focus' ? '#5e72e4' : 'transparent',
                      color: viewMode === 'focus' ? '#ffffff' : '#64748b',
                      boxShadow: viewMode === 'focus' ? '0 2px 8px rgba(94, 114, 228, 0.3)' : 'none',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaPen className="mr-1" size={10} /> Filters Only
                  </Button>
                  <Button
                    size="sm"
                    className="border-0 px-2 py-1 font-weight-bold mb-0 ml-1"
                    onClick={() => setViewMode('split')}
                    style={{
                      borderRadius: '6px',
                      fontSize: '11px',
                      textTransform: 'none',
                      backgroundColor: viewMode === 'split' ? '#5e72e4' : 'transparent',
                      color: viewMode === 'split' ? '#ffffff' : '#64748b',
                      boxShadow: viewMode === 'split' ? '0 2px 8px rgba(94, 114, 228, 0.3)' : 'none',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaColumns className="mr-1" size={10} /> Split View
                  </Button>
                </div>
              </Col>
            </Row>

            <Row>
              {/* FILTERS & DYNAMIC COMPOSER */}
              <Col lg={viewMode === 'focus' ? '12' : '5'} className="mb-4" style={{ transition: 'all 0.3s ease' }}>
                <Card className="shadow border-0 card-premium">
                  <CardHeader className="bg-transparent border-0 d-flex align-items-center pb-0 pt-3 px-3">
                    <div
                      className="mr-2 d-flex align-items-center justify-content-center"
                      style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        backgroundColor: '#f3e8ff',
                        color: '#a855f7'
                      }}
                    >
                      <FaFileAlt size={11} />
                    </div>
                    <div>
                      <h6 className="mb-0 font-weight-bold text-dark text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.4px' }}>Filters &amp; Composer</h6>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-3 px-3 pb-3">
                    <Form>
                      {/* Template builder */}
                      <h6 className="heading-small text-muted mb-2 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>1. Message Customizer</h6>
                      <FormGroup className="mb-3">
                        <div className="mb-2 d-flex flex-wrap p-1 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', gap: '2px' }}>
                          {[
                            { name: 'Student Name', placeholder: '{studentName}' },
                            { name: 'Section Name', placeholder: '{sectionName}' },
                            { name: 'Class Key', placeholder: '{classKey}' },
                            { name: 'Zoom Link', placeholder: '{zoomLink}' },
                            { name: 'Start Date', placeholder: '{startDate}' },
                            { name: 'Teacher Name', placeholder: '{teacherName}' },
                            { name: 'Phone', placeholder: '{mobileNumber}' },
                          ].map((v) => (
                            <Button
                              key={v.placeholder}
                              color="secondary"
                              size="xs"
                              onClick={() => insertVariable(v.placeholder)}
                              className="btn-pill-premium text-dark bg-white border"
                              style={{
                                fontSize: '10px',
                                padding: '3px 9px',
                                textTransform: 'none',
                                borderColor: '#cbd5e1',
                                margin: '2px'
                              }}
                            >
                              + {v.name}
                            </Button>
                          ))}
                        </div>
                        <Input
                          type="textarea"
                          rows="4"
                          value={messageTemplate}
                          onChange={(e) => setMessageTemplate(e.target.value)}
                          className="form-control-alternative font-family-monospace text-dark"
                          placeholder="Compose your dynamic message here..."
                          style={{
                            fontSize: '12px',
                            lineHeight: '1.5',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            color: '#1e293b',
                            padding: '8px 10px',
                            transition: 'all 0.2s ease',
                          }}
                        />
                      </FormGroup>

                      {/* Class filters */}
                      <h6 className="heading-small text-muted mb-1 font-weight-bold" style={{ fontSize: '10px', letterSpacing: '0.4px' }}>2. Class Categorization</h6>
                      <div className="d-flex flex-wrap mb-3 bg-white p-2 rounded border" style={{ borderRadius: '8px', gap: '2px' }}>
                        {['Coding', 'Chess', 'Maths', 'SpeakClub'].map((key) => {
                          const activeColors = {
                            Coding: { bg: '#5e72e4', text: '#ffffff' },
                            Chess: { bg: '#2d3748', text: '#ffffff' },
                            Maths: { bg: '#2dce89', text: '#ffffff' },
                            SpeakClub: { bg: '#f5365c', text: '#ffffff' }
                          };
                          const isActive = selectedClasses[key];
                          return (
                            <Button
                              key={key}
                              size="sm"
                              onClick={() => handleClassToggle(key)}
                              className="m-1 btn-pill-premium"
                              style={{
                                backgroundColor: isActive ? activeColors[key]?.bg || '#5e72e4' : '#f8fafc',
                                color: isActive ? activeColors[key]?.text || '#ffffff' : '#64748b',
                                border: isActive ? 'none' : '1px solid #e2e8f0',
                                boxShadow: isActive ? '0 4px 10px rgba(0,0,0,0.08)' : 'none',
                              }}
                            >
                              {accountLabel(key)}
                            </Button>
                          );
                        })}
                      </div>

                      {/* Subscription status filters */}
                      <h6 className="heading-small text-muted mb-1 font-weight-bold" style={{ fontSize: '10px', letterSpacing: '0.4px' }}>3. Subscription State</h6>
                      <div className="d-flex flex-wrap mb-3 bg-white p-2 rounded border" style={{ borderRadius: '8px', gap: '2px' }}>
                        {[
                          { label: 'Current (Active)', key: 'active', color: '#2dce89' },
                          { label: 'Expired', key: 'expired', color: '#f5365c' },
                          { label: 'Sales Leads', key: 'sales', color: '#fb6340' }
                        ].map((v) => {
                          const isActive = selectedStatuses[v.key];
                          return (
                            <Button
                              key={v.key}
                              size="sm"
                              onClick={() => handleStatusToggle(v.key)}
                              className="m-1 btn-pill-premium"
                              style={{
                                backgroundColor: isActive ? v.color : '#f8fafc',
                                color: isActive ? '#ffffff' : '#64748b',
                                border: isActive ? 'none' : '1px solid #e2e8f0',
                                boxShadow: isActive ? `0 4px 10px rgba(0,0,0,0.08)` : 'none',
                              }}
                            >
                              {v.label}
                            </Button>
                          );
                        })}
                      </div>

                      {/* Simulation mode toggle */}
                      <h6 className="heading-small text-muted mb-1 font-weight-bold" style={{ fontSize: '10px', letterSpacing: '0.4px' }}>4. Sandbox Mode</h6>
                      <div
                        onClick={() => setSimulate(!simulate)}
                        className="d-flex align-items-center justify-content-between mb-3 p-2 rounded border"
                        style={{
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: simulate ? '#f5f7ff' : '#fff8f8',
                          borderColor: simulate ? '#dce2ff' : '#ffe2e2',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="mr-2 d-flex align-items-center justify-content-center"
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '6px',
                              backgroundColor: simulate ? '#5e72e4' : '#f5365c',
                              color: '#ffffff',
                              fontSize: '12px'
                            }}
                          >
                            {simulate ? <FaShieldAlt size={12} /> : <FaRocket size={12} />}
                          </div>
                          <div>
                            <span className="font-weight-bold text-dark d-block" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                              {simulate ? 'Sandbox Mode' : 'Live Delivery'}
                            </span>
                            <small className="text-muted d-block" style={{ fontSize: '10px', lineHeight: '1.1' }}>
                              {simulate ? 'Twilio bypassed' : 'Sends real messages'}
                            </small>
                          </div>
                        </div>
                        <div
                          style={{
                            width: '42px',
                            height: '22px',
                            borderRadius: '12px',
                            backgroundColor: simulate ? '#2dce89' : '#cbd5e1',
                            padding: '2px',
                            position: 'relative',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <div
                            style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              backgroundColor: '#ffffff',
                              position: 'absolute',
                              left: simulate ? '22px' : '2px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </div>
                      </div>

                      {/* Advanced Template Key Mapping collapsible UI */}
                      <div className="mb-3">
                        <div
                          className="d-flex align-items-center justify-content-between p-2 bg-white rounded border border-light"
                          style={{ cursor: 'pointer', borderRadius: '8px' }}
                          onClick={() => setShowTemplateMapping(!showTemplateMapping)}
                        >
                          <span className="font-weight-bold text-dark mb-0 d-flex align-items-center" style={{ fontSize: '11px' }}>
                            <FaKey className="text-primary mr-1" size={11} /> Configure Key Mapping
                          </span>
                          <span className="text-muted" style={{ fontSize: '10px' }}>{showTemplateMapping ? '▲' : '▼'}</span>
                        </div>

                        {showTemplateMapping && (
                          <div className="mt-1 bg-white p-2 rounded border custom-scroll" style={{ maxHeight: '360px', overflowY: 'auto', borderRadius: '8px' }}>
                            <small className="text-muted d-block mb-3" style={{ lineHeight: '1.4' }}>
                              Select a subscription state tab below to configure mapped API keys for placeholders (e.g. <code>{"{studentName}"}</code>) specifically for that recipient type.
                            </small>

                            {/* Sub-tabs for each subscription state */}
                            <Nav className="nav-fill flex-row mb-3 bg-secondary p-1 rounded" style={{ border: '1px solid #dee2e6' }}>
                              <NavItem>
                                <NavLink
                                  className={classnames('py-1 font-weight-bold text-xs border-0', {
                                    active: mappingTab === 'active',
                                  })}
                                  onClick={() => setMappingTab('active')}
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                    backgroundColor: mappingTab === 'active' ? '#5e72e4' : 'transparent',
                                    color: mappingTab === 'active' ? '#ffffff' : '#525f7f',
                                    boxShadow: mappingTab === 'active' ? '0 2px 4px rgba(94, 114, 228, 0.4)' : 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  Active (Current)
                                </NavLink>
                              </NavItem>
                              <NavItem>
                                <NavLink
                                  className={classnames('py-1 font-weight-bold text-xs border-0', {
                                    active: mappingTab === 'expired',
                                  })}
                                  onClick={() => setMappingTab('expired')}
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                    backgroundColor: mappingTab === 'expired' ? '#f5365c' : 'transparent',
                                    color: mappingTab === 'expired' ? '#ffffff' : '#525f7f',
                                    boxShadow: mappingTab === 'expired' ? '0 2px 4px rgba(245, 54, 92, 0.4)' : 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  Expired
                                </NavLink>
                              </NavItem>
                              <NavItem>
                                <NavLink
                                  className={classnames('py-1 font-weight-bold text-xs border-0', {
                                    active: mappingTab === 'sales_lead',
                                  })}
                                  onClick={() => setMappingTab('sales_lead')}
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                    backgroundColor: mappingTab === 'sales_lead' ? '#fb6340' : 'transparent',
                                    color: mappingTab === 'sales_lead' ? '#ffffff' : '#525f7f',
                                    boxShadow: mappingTab === 'sales_lead' ? '0 2px 4px rgba(251, 99, 64, 0.4)' : 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  Sales Lead
                                </NavLink>
                              </NavItem>
                            </Nav>

                            {(() => {
                              // Dynamic extraction of placeholders from messageTemplate
                              const placeholders = [...new Set([...messageTemplate.matchAll(/{([^}]+)}/g)].map(m => m[1]))];
                              if (placeholders.length === 0) {
                                return (
                                  <div className="text-center py-4 text-muted border-0" style={{ fontSize: '12px' }}>
                                    <FaInfoCircle className="mr-1 text-primary animate-pulse" size={14} />
                                    No dynamic variables (like <code>{"{studentName}"}</code>) found in your template.
                                  </div>
                                );
                              }

                              const friendlyNames = {
                                studentName: 'Student Name',
                                sectionName: 'Section Name',
                                classKey: 'Class Key',
                                zoomLink: 'Zoom Link',
                                startDate: 'Start Date',
                                teacherName: 'Teacher Name',
                                mobileNumber: 'Phone Number'
                              };

                              return placeholders.map((placeholder) => {
                                const label = friendlyNames[placeholder]
                                  ? `${friendlyNames[placeholder]} ({${placeholder}})`
                                  : `Custom Placeholder ({${placeholder}})`;

                                const currentValue = templateKeyMap[mappingTab]?.[placeholder] !== undefined
                                  ? templateKeyMap[mappingTab][placeholder]
                                  : placeholder;

                                const standardKeys = ['studentName', 'name', 'email', 'phone', 'mobileNumber', 'mobile', 'sectionName', 'zoomLink', 'startDate', 'startTime', 'teacherName', 'createdBy', 'classId', 'sectionId', 'stripeSubscriptionStatus', 'classKey'];
                                const isCustomMode = explicitCustomKeys[`${mappingTab}_${placeholder}`] || (currentValue !== '' && !standardKeys.includes(currentValue));

                                return (
                                  <FormGroup key={placeholder} className="mapping-row mb-2">
                                    <Label className="text-dark mb-0 font-weight-bold pr-2" style={{ fontSize: '12px' }}>
                                      {label}
                                    </Label>
                                    <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                      {isCustomMode ? (
                                        <Input
                                          type="text"
                                          size="sm"
                                          value={currentValue}
                                          onChange={(e) => handleTemplateKeyMapChange(mappingTab, placeholder, e.target.value)}
                                          placeholder={`API Key (e.g. parent_name)`}
                                          className="bg-white border text-dark font-weight-bold"
                                          style={{ height: '30px', width: '150px', fontSize: '11px' }}
                                        />
                                      ) : (
                                        <Input
                                          type="select"
                                          size="sm"
                                          value={currentValue}
                                          onChange={(e) => handleTemplateKeyMapChange(mappingTab, placeholder, e.target.value)}
                                          className="py-0"
                                          style={{ height: '30px', width: '150px', fontSize: '11px', fontWeight: 'bold' }}
                                        >
                                          <option value="studentName">studentName (Student Name)</option>
                                          <option value="name">name (Lead Name)</option>
                                          <option value="email">email (Email Address)</option>
                                          <option value="phone">phone (Resolved phone)</option>
                                          <option value="mobileNumber">mobileNumber (Section Mobile)</option>
                                          <option value="mobile">mobile (Lead Mobile)</option>
                                          <option value="sectionName">sectionName (Section Name)</option>
                                          <option value="zoomLink">zoomLink (Zoom Class Link)</option>
                                          <option value="startDate">startDate (Section Start Date)</option>
                                          <option value="startTime">startTime (Lead Time)</option>
                                          <option value="teacherName">teacherName (Coach Name)</option>
                                          <option value="createdBy">createdBy (Lead Creator)</option>
                                          <option value="classId">classId (Class ID)</option>
                                          <option value="sectionId">sectionId (Section/Lead ID)</option>
                                          <option value="stripeSubscriptionStatus">stripeSubscriptionStatus (Subscription State)</option>
                                          <option value="classKey">classKey (Mapped custom category)</option>
                                        </Input>
                                      )}
                                      <Button
                                        size="sm"
                                        color="secondary"
                                        className="p-1 d-flex align-items-center justify-content-center border"
                                        onClick={() => {
                                          const customKey = `${mappingTab}_${placeholder}`;
                                          if (isCustomMode) {
                                            setExplicitCustomKeys({ ...explicitCustomKeys, [customKey]: false });
                                            handleTemplateKeyMapChange(mappingTab, placeholder, placeholder);
                                          } else {
                                            setExplicitCustomKeys({ ...explicitCustomKeys, [customKey]: true });
                                          }
                                        }}
                                        style={{
                                          height: '30px',
                                          width: '30px',
                                          borderRadius: '6px',
                                          backgroundColor: '#f8fafc',
                                          borderColor: '#cbd5e1'
                                        }}
                                        title={isCustomMode ? "Switch to dropdown selection" : "Switch to custom free-form API key typing"}
                                      >
                                        {isCustomMode ? <FaList size={11} className="text-primary" /> : <FaKeyboard size={11} className="text-primary" />}
                                      </Button>
                                    </div>
                                  </FormGroup>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>

                      <div className="d-flex justify-content-center mt-3">
                        <Button
                          block
                          size="sm"
                          onClick={() => fetchRecipients(false)}
                          disabled={loadingRecipients}
                          className="shadow-sm py-2 font-weight-bold text-uppercase border-0 text-white"
                          style={{
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)',
                            boxShadow: '0 2px 8px rgba(94, 114, 228, 0.3)',
                            letterSpacing: '0.3px',
                            fontSize: '11px'
                          }}
                        >
                          {loadingRecipients ? <Spinner size="sm" className="mr-2" /> : null}
                          Sync & Refresh Recipient Data
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              {/* RECIPIENTS PREVIEW TABLE & CAMPAIGN STATS */}
              {viewMode === 'split' && (
                <Col lg="7" className="mb-4">
                {/* Sending progress alert */}
                {campaignProgress.active && (
                  <Card className="shadow border-0 bg-gradient-info text-white mb-3">
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 text-white font-weight-bold text-uppercase d-flex align-items-center" style={{ fontSize: '12px' }}>
                          <Spinner size="sm" className="mr-2" />
                          Sending Campaign...
                        </h6>
                        <span className="font-weight-bold" style={{ fontSize: '12px' }}>{Math.round(((campaignProgress.success + campaignProgress.failed) / campaignProgress.total) * 100)}%</span>
                      </div>
                      <Progress
                        value={((campaignProgress.success + campaignProgress.failed) / campaignProgress.total) * 100}
                        color="success"
                        className="progress-xs mb-3 bg-white"
                      />
                      <div className="d-flex justify-content-around text-center mt-1">
                        <div>
                          <small className="d-block text-light text-uppercase" style={{ fontSize: '9px' }}>Total</small>
                          <span className="h5 font-weight-bold text-white">{campaignProgress.total}</span>
                        </div>
                        <div className="border-left border-light mx-2" />
                        <div>
                          <small className="d-block text-light text-uppercase" style={{ fontSize: '9px' }}>Success</small>
                          <span className="h5 font-weight-bold text-white">{campaignProgress.success}</span>
                        </div>
                        <div className="border-left border-light mx-2" />
                        <div>
                          <small className="d-block text-light text-uppercase" style={{ fontSize: '9px' }}>Failed</small>
                          <span className="h5 font-weight-bold text-danger">{campaignProgress.failed}</span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                <Card className="shadow border-0 card-premium">
                  <CardHeader className="bg-transparent border-0 pb-2 border-bottom px-3 pt-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2" style={{ gap: '10px' }}>
                      <div className="d-flex align-items-center">
                        <div
                          className="mr-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '6px',
                            backgroundColor: '#e8fdf4',
                            color: '#2dce89'
                          }}
                        >
                          <FaUserCheck size={12} />
                        </div>
                        <div>
                          <h6 className="mb-0 font-weight-bold text-dark text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.4px' }}>Recipients Live Preview</h6>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap align-items-center" style={{ gap: '6px' }}>
                        <Badge color="info" className="px-2 py-1 text-uppercase font-weight-bold" style={{ borderRadius: '6px', fontSize: '9px' }}>
                          Total: {filteredRecipients.length}
                        </Badge>
                        <Badge color="success" className="px-2 py-1 text-uppercase font-weight-bold" style={{ borderRadius: '6px', fontSize: '9px' }}>
                          To Send: {filteredRecipients.filter(r => !!selectedRecipients[`${r.email}_${r.sectionId}`]).length}
                        </Badge>
                        <Badge color="danger" className="px-2 py-1 text-uppercase font-weight-bold" style={{ borderRadius: '6px', fontSize: '9px' }}>
                          Excluded: {filteredRecipients.filter(r => !selectedRecipients[`${r.email}_${r.sectionId}`]).length}
                        </Badge>
                        <Button
                          size="xs"
                          color={hideMessageDetails ? "info" : "warning"}
                          outline
                          onClick={() => setHideMessageDetails(!hideMessageDetails)}
                          className="btn-pill-premium mb-0"
                          style={{ fontSize: '9px', padding: '2px 8px', textTransform: 'none' }}
                        >
                          {hideMessageDetails ? "Show Message Preview" : "Hide Message Preview"}
                        </Button>
                      </div>
                    </div>
                    {hasFetched && filteredRecipients.length > 0 && (
                      <div className="mt-2">
                        <InputGroup size="sm" className="shadow-sm border rounded" style={{ overflow: 'hidden' }}>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText className="bg-light border-0 text-muted px-2 font-weight-bold" style={{ fontSize: '11px' }}>
                              🔍 Filter:
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="text"
                            placeholder="Exclude folks by searching Name, Phone, or Email..."
                            value={recipientSearchQuery}
                            onChange={(e) => {
                              setRecipientSearchQuery(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="border-0 bg-white"
                            style={{ fontSize: '12px', height: '32px' }}
                          />
                          {recipientSearchQuery && (
                            <InputGroupAddon addonType="append">
                              <Button
                                size="sm"
                                color="secondary"
                                className="border-0 bg-white text-muted px-2 py-0"
                                onClick={() => setRecipientSearchQuery('')}
                                style={{ height: '32px', fontSize: '11px' }}
                              >
                                Clear
                              </Button>
                            </InputGroupAddon>
                          )}
                        </InputGroup>
                      </div>
                    )}
                  </CardHeader>

                  {loadingRecipients ? (
                    <CardBody className="text-center py-4">
                      <Spinner color="primary" style={{ width: '2rem', height: '2rem' }} />
                      <p className="mt-2 text-muted" style={{ fontSize: '12px' }}>Resolving recipient details...</p>
                    </CardBody>
                  ) : (
                    <>
                      <div className="table-responsive" style={{ maxHeight: '550px' }}>
                        <Table className="align-items-center table-flush table-hover table-responsive-mobile" responsive>
                          <thead className="thead-light">
                            <tr>
                              <th scope="col" style={{ width: '40px' }} className="text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    currentItems.length > 0 &&
                                    currentItems.every((r) => !!selectedRecipients[`${r.email}_${r.sectionId}`])
                                  }
                                  onChange={handleSelectAll}
                                  style={{ position: 'relative', cursor: 'pointer', width: '18px', height: '18px', accentColor: '#5e72e4', zIndex: 1 }}
                                />
                              </th>
                              <th scope="col">User & Class Details</th>
                              {!hideMessageDetails && <th scope="col">Message Live Preview</th>}
                              <th scope="col">Recipient Phone</th>
                              <th scope="col" className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!hasFetched ? (
                              <tr className="no-cards">
                                <td colSpan={hideMessageDetails ? "4" : "5"} className="text-center py-6 text-muted border-0">
                                  <FaInfoCircle className="mr-1 text-primary mb-2" size={24} />
                                  <div className="font-weight-bold text-dark animate-pulse" style={{ fontSize: '15px' }}>
                                    Ready to Preview Recipients?
                                  </div>
                                  <div className="mt-1 px-4 text-center" style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                    First customize your message template, configure class & subscription filters on the left, and then click <strong>"Sync & Refresh Recipient Data"</strong> to render live personalized previews.
                                  </div>
                                </td>
                              </tr>
                            ) : searchedRecipients.length === 0 ? (
                              <tr className="no-cards">
                                <td colSpan={hideMessageDetails ? "4" : "5"} className="text-center py-5 text-muted border-0">
                                  <FaInfoCircle className="mr-1" />
                                  {filteredRecipients.length === 0 
                                    ? "No users match the selected filter configuration. Adjust the filters and refresh."
                                    : "No recipients found matching your search query."}
                                </td>
                              </tr>
                            ) : (
                              currentItems.map((r, idx) => {
                                const itemKey = `${r.email}_${r.sectionId}`;
                                const isSelected = !!selectedRecipients[itemKey];
                                const classKey = classKeyMap[r.classId] || 'Coding';
                                const isEditing = editingRowKey === itemKey;
                                return (
                                        <tr key={itemKey} className={classnames({ 'bg-secondary-light': !isSelected, 'bg-info-light': isEditing })}>
                                          <td className="text-center checkbox-cell" data-label="Include Recipient">
                                            {!isEditing && (
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleSelectRecipient(itemKey)}
                                                style={{ position: 'relative', cursor: 'pointer', width: '18px', height: '18px', accentColor: '#5e72e4', zIndex: 1 }}
                                              />
                                            )}
                                          </td>
                                          <td data-label="User &amp; Class Details">
                                            {isEditing ? (
                                              <FormGroup className="mb-2">
                                                <small className="font-weight-bold text-muted d-block mb-1">Student Name</small>
                                                <Input
                                                  type="text"
                                                  size="sm"
                                                  value={editForm.studentName}
                                                  onChange={(e) => setEditForm({ ...editForm, studentName: e.target.value })}
                                                  className="bg-white border text-dark font-weight-bold"
                                                />
                                                <small className="text-muted d-block mt-1">{r.email}</small>
                                              </FormGroup>
                                            ) : (
                                              <>
                                                <div className="font-weight-bold text-dark mb-0" style={{ fontSize: '13.5px' }}>{r.studentName}</div>
                                                <small className="text-muted d-block">{r.email}</small>
                                              </>
                                            )}
                                            <div className="mt-1 d-flex align-items-center">
                                              <Badge color="light" className="text-dark border mr-1 py-1 px-2">{accountLabel(classKey)}</Badge>
                                              <Badge color={r.stripeSubscriptionStatus === 'active' ? 'success' : r.stripeSubscriptionStatus === 'sales_lead' ? 'warning' : 'danger'} className="py-1 px-2">
                                                {r.stripeSubscriptionStatus === 'active' ? 'Current' : r.stripeSubscriptionStatus === 'sales_lead' ? 'Sales Lead' : 'Expired'}
                                              </Badge>
                                            </div>
                                          </td>
                                          {!hideMessageDetails && (
                                            <td style={{ minWidth: '220px', whiteSpace: 'normal' }} data-label="Message Live Preview">
                                              <div className="d-flex align-items-center mb-1">
                                                <Badge color="info" outline className="py-0 px-1 font-weight-bold" style={{ fontSize: '9px', borderRadius: '4px', textTransform: 'none' }}>
                                                  SMS Body Preview
                                                </Badge>
                                              </div>
                                              <div className="bg-light p-2 rounded text-dark border" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', lineHeight: '1.4', fontFamily: 'monospace' }}>
                                                {interpolateMessage(messageTemplate, isEditing ? { ...r, ...editForm } : r)}
                                              </div>
                                            </td>
                                          )}
                                          <td data-label="Recipient Phone / Session Info">
                                            {isEditing ? (
                                              <div style={{ maxWidth: '280px' }}>
                                                <FormGroup className="mb-2">
                                                  <small className="font-weight-bold text-muted d-block mb-1">Phone Number</small>
                                                  <Input
                                                    type="text"
                                                    size="sm"
                                                    value={editForm.phone}
                                                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                                    className="bg-white border text-dark"
                                                  />
                                                  <small className="text-success d-block font-italic mt-1" style={{ fontSize: '10px' }}>
                                                    Cleans to: {formatPhoneNumber(editForm.phone)}
                                                  </small>
                                                </FormGroup>
                                                <FormGroup className="mb-2">
                                                  <small className="font-weight-bold text-muted d-block mb-1">Zoom Link</small>
                                                  <Input
                                                    type="text"
                                                    size="sm"
                                                    value={editForm.zoomLink}
                                                    onChange={(e) => setEditForm({ ...editForm, zoomLink: e.target.value })}
                                                    className="bg-white border text-dark"
                                                  />
                                                </FormGroup>
                                                <FormGroup className="mb-2">
                                                  <small className="font-weight-bold text-muted d-block mb-1">Start Date</small>
                                                  <Input
                                                    type="text"
                                                    size="sm"
                                                    value={editForm.startDate}
                                                    onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                                                    className="bg-white border text-dark"
                                                  />
                                                </FormGroup>
                                                <FormGroup className="mb-2">
                                                  <small className="font-weight-bold text-muted d-block mb-1">Coach Name</small>
                                                  <Input
                                                    type="text"
                                                    size="sm"
                                                    value={editForm.teacherName}
                                                    onChange={(e) => setEditForm({ ...editForm, teacherName: e.target.value })}
                                                    className="bg-white border text-dark"
                                                  />
                                                </FormGroup>
                                              </div>
                                            ) : (
                                              <>
                                                <div className="d-flex align-items-center mb-1">
                                                  <Badge color="success" outline className="py-0 px-1 font-weight-bold" style={{ fontSize: '9px', borderRadius: '4px', textTransform: 'none' }}>
                                                    E.164 Twilio Target
                                                  </Badge>
                                                </div>
                                                <div className="font-weight-bold text-dark mb-0" style={{ fontSize: '13.5px', fontFamily: 'monospace' }}>
                                                  {formatPhoneNumber(r.phone)}
                                                </div>
                                                {r.phone && r.phone !== formatPhoneNumber(r.phone) && (
                                                  <small className="text-muted d-block font-italic mb-1" style={{ fontSize: '10px' }}>
                                                    Raw DB: {r.phone}
                                                  </small>
                                                )}
                                                <small className="text-muted d-block mt-1">Section: {r.sectionName}</small>
                                                {r.zoomLink && <small className="text-primary d-block font-weight-bold">Zoom: {r.zoomLink}</small>}
                                                {r.startDate && <small className="text-muted d-block">Start: {moment(r.startDate).isValid() ? moment(r.startDate).format('YYYY-MM-DD') : r.startDate}</small>}
                                                {r.teacherName && <small className="text-muted d-block">Coach: {r.teacherName}</small>}
                                              </>
                                            )}
                                          </td>
                                          <td className="text-center action-cell">
                                            {isEditing ? (
                                              <div className="d-flex flex-column align-items-center w-100">
                                                <Button
                                                  color="success"
                                                  size="sm"
                                                  onClick={() => saveRowEdit(itemKey)}
                                                  className="mb-1 w-100 font-weight-bold py-1"
                                                >
                                                  Save
                                                </Button>
                                                <Button
                                                  color="secondary"
                                                  size="sm"
                                                  onClick={cancelRowEdit}
                                                  className="w-100 font-weight-bold py-1"
                                                >
                                                  Cancel
                                                </Button>
                                              </div>
                                            ) : (
                                              <Button
                                                color="primary"
                                                outline
                                                size="xs"
                                                onClick={() => startEditingRow(r)}
                                                className="font-weight-bold text-nowrap d-flex align-items-center justify-content-center"
                                                style={{ textTransform: 'none', borderRadius: '4px' }}
                                              >
                                                <FaPen className="mr-1" size={10} /> Edit
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })
                            )}
                          </tbody>
                        </Table>
                      </div>

                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center py-3 bg-secondary-light border-top border-bottom">
                          <Button
                            color="secondary"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            className="font-weight-bold text-dark px-3 py-1 mb-0"
                            style={{ borderRadius: '15px' }}
                          >
                            &larr; Prev
                          </Button>
                          <span className="mx-4 font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                            Page {currentPage} of {totalPages}
                            <span className="text-muted ml-2">({searchedRecipients.length} total)</span>
                          </span>
                          <Button
                            color="secondary"
                            size="sm"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            className="font-weight-bold text-dark px-3 py-1 mb-0"
                            style={{ borderRadius: '15px' }}
                          >
                            Next &rarr;
                          </Button>
                        </div>
                      )}

                      <CardFooter className="bg-transparent border-top d-flex align-items-center justify-content-between p-2" style={{ gap: '8px' }}>
                        {/* Save Campaign as Draft - PRIMARY */}
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => {
                            setDraftName(editingDraftId ? draftName : '');
                            setShowSaveModal(true);
                          }}
                          disabled={savingDraft}
                          className="font-weight-bold flex-grow-1"
                          style={{ fontSize: '11px', borderRadius: '6px' }}
                        >
                          <FaSave className="mr-1" size={11} />
                          {editingDraftId ? 'Update Draft' : 'Save Campaign'}
                        </Button>

                        {/* Send Campaign - SECONDARY */}
                        <Button
                          color="success"
                          outline
                          size="sm"
                          onClick={handleSendCampaign}
                          disabled={sendingMessages || filteredRecipients.filter(r => !!selectedRecipients[`${r.email}_${r.sectionId}`]).length === 0}
                          className="font-weight-bold"
                          style={{ fontSize: '11px', borderRadius: '6px', minWidth: '120px' }}
                        >
                          <FaPaperPlane className="mr-1" size={10} />
                          {sendingMessages ? 'Sending...' : `Send (${filteredRecipients.filter(r => !!selectedRecipients[`${r.email}_${r.sectionId}`]).length})`}
                        </Button>
                      </CardFooter>

                      {/* Inline Save Name Modal */}
                      {showSaveModal && (
                        <div
                          className="position-fixed d-flex align-items-center justify-content-center"
                          style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999 }}
                          onClick={() => setShowSaveModal(false)}
                        >
                          <div
                            className="bg-white rounded shadow-lg p-4"
                            style={{ minWidth: '320px', maxWidth: '90vw', borderRadius: '12px' }}
                            onClick={e => e.stopPropagation()}
                          >
                            <h6 className="font-weight-bold text-dark mb-3" style={{ fontSize: '13px' }}>
                              <FaBookmark className="text-primary mr-2" size={13} />
                              {editingDraftId ? 'Update Campaign Name' : 'Save Campaign'}
                            </h6>
                            <Input
                              type="text"
                              placeholder="Campaign name (e.g. June Active Renewal)"
                              value={draftName}
                              onChange={e => setDraftName(e.target.value)}
                              autoFocus
                              className="mb-3"
                              style={{ fontSize: '12px', borderRadius: '6px' }}
                              onKeyDown={e => e.key === 'Enter' && handleSaveDraft()}
                            />
                            <div className="d-flex" style={{ gap: '8px' }}>
                              <Button color="primary" size="sm" onClick={handleSaveDraft} disabled={savingDraft || !draftName.trim()} className="flex-grow-1 font-weight-bold" style={{ fontSize: '11px' }}>
                                {savingDraft ? <Spinner size="sm" className="mr-1" /> : <FaSave className="mr-1" size={10} />}
                                {editingDraftId ? 'Update' : 'Save'}
                              </Button>
                              <Button color="secondary" size="sm" onClick={() => setShowSaveModal(false)} className="font-weight-bold" style={{ fontSize: '11px' }}>Cancel</Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card>
              </Col>
              )}
            </Row>
          </TabPane>

          {/* CAMPAIGN HISTORY TAB */}
          <TabPane tabId="history">
            <Card className="shadow border-0">
              <CardHeader className="bg-transparent border-0">
                <div className="d-flex align-items-center">
                  <FaHistory className="text-purple mr-2" size={18} />
                  <h4 className="mb-0 font-weight-bold text-dark text-uppercase">Bulk Message Dispatch Logs</h4>
                </div>
              </CardHeader>
              <div className="table-responsive">
                <Table className="align-items-center table-flush table-hover table-responsive-mobile" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Campaign ID / Date</th>
                      <th scope="col">Message Template</th>
                      <th scope="col">Filters Selected</th>
                      <th scope="col" className="text-center">Delivery Progress</th>
                      <th scope="col" className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 ? (
                      <tr className="no-cards">
                        <td colSpan="5" className="text-center py-5 text-muted border-0">
                          <FaInfoCircle className="mr-1" />
                          No campaigns found. Make your first bulk transmission!
                        </td>
                      </tr>
                    ) : (
                      campaigns.map((c) => {
                        const isExpanded = expandedCampaign === c.campaignId;
                        const percent = c.totalCount > 0 ? Math.round((c.successCount / c.totalCount) * 100) : 0;
                        return (
                          <React.Fragment key={c.campaignId}>
                            <tr style={c.promotedLiveCampaignId ? { backgroundColor: '#f0fdf4' } : {}}>
                              <td data-label="Campaign ID / Date">
                                <div className="font-weight-bold text-dark" style={{ fontSize: '13.5px' }}>{c.campaignId}</div>
                                <small className="text-muted d-block">{moment(c.createdAt).format('YYYY-MM-DD hh:mm a')}</small>
                                {c.promotedLiveCampaignId && (
                                  <Badge color="success" className="mt-1 d-block" style={{ width: 'fit-content' }}>
                                    Promoted Live
                                  </Badge>
                                )}
                              </td>
                              <td style={{ maxWidth: '280px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} data-label="Template Preview">
                                <span className="text-dark" style={{ fontSize: '13px' }}>{c.messageTemplate}</span>
                              </td>
                              <td data-label="Filters Selected">
                                <div className="mb-1">
                                  <small className="font-weight-bold mr-1">Classes:</small>
                                  {c.classFilters.map((f) => (
                                    <Badge color="secondary" className="mr-1 py-1 text-dark" key={f}>{accountLabel(f)}</Badge>
                                  ))}
                                </div>
                                <div>
                                  <small className="font-weight-bold mr-1">Status:</small>
                                  {c.subscriptionFilters.map((f) => (
                                    <Badge color="light" className="mr-1 py-1 text-dark border" key={f}>
                                      {f === 'active' ? 'Current' : 'Expired'}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td data-label="Progress Status">
                                <div className="text-center font-weight-bold text-dark mb-1" style={{ fontSize: '13.5px' }}>
                                  {c.successCount} / {c.totalCount} Success
                                </div>
                                <Progress value={percent} color="success" className="progress-xs bg-secondary" />
                                {c.failedCount > 0 && (
                                  <small className="text-danger d-block text-center mt-1">
                                    {c.failedCount} Failed
                                  </small>
                                )}
                              </td>
                              <td className="text-center action-cell">
                                <div className="d-flex justify-content-center align-items-center" style={{ gap: '6px' }}>
                                  <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => {
                                      if (!isExpanded) {
                                        fetchCampaignRecipients(c.campaignId);
                                      }
                                      setExpandedCampaign(isExpanded ? null : c.campaignId);
                                    }}
                                    className="font-weight-bold"
                                    style={{ fontSize: '11px', textTransform: 'none', borderRadius: '4px' }}
                                  >
                                    {isExpanded ? 'Hide Details' : 'Expand Logs'}
                                  </Button>
                                  
                                  {c.campaignId.startsWith('BULK_SIM_') && (
                                    !c.promotedLiveCampaignId ? (
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleSendLiveFromSimulation(c)}
                                        className="font-weight-bold d-flex align-items-center justify-content-center text-white"
                                        style={{ fontSize: '11px', textTransform: 'none', borderRadius: '4px', padding: '6px 12px' }}
                                      >
                                        <FaRocket className="mr-1" size={10} /> Send Live Now
                                      </Button>
                                    ) : (
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        disabled
                                        className="font-weight-bold d-flex align-items-center justify-content-center"
                                        style={{ fontSize: '11px', textTransform: 'none', borderRadius: '4px', padding: '6px 12px' }}
                                      >
                                        <FaCheck className="mr-1" size={10} /> Dispatched
                                      </Button>
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Expandable delivery log details */}
                            {isExpanded && (
                              <tr className="no-cards">
                                <td colSpan="5" className="bg-light p-2 p-md-4 border-0">
                                  <div className="bg-white rounded p-2 p-md-3 border shadow-sm">
                                    <h5 className="mb-3 font-weight-bold text-dark text-uppercase">Recipient Log Audit</h5>
                                    {loadingCampaignRecipients[c.campaignId] ? (
                                      <div className="text-center py-5">
                                        <Spinner color="primary" style={{ width: '2rem', height: '2rem' }} />
                                        <p className="mt-2 text-muted" style={{ fontSize: '12px' }}>Loading recipient logs...</p>
                                      </div>
                                    ) : !c.recipients || c.recipients.length === 0 ? (
                                      <div className="text-center py-5 text-muted">
                                        <FaInfoCircle className="mr-1" /> No recipient log records found for this campaign.
                                      </div>
                                    ) : (
                                      <>
                                        <Table className="align-items-center table-flush table-hover bg-white border table-responsive-mobile" responsive>
                                          <thead className="thead-light">
                                            <tr>
                                              <th>Student</th>
                                              <th>Email / Phone</th>
                                              <th>Custom Message Sent</th>
                                              <th className="text-center">Status</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(() => {
                                              const currentLogPage = campaignLogPages[c.campaignId] || 1;
                                              const logIndexLast = currentLogPage * logItemsPerPage;
                                              const logIndexFirst = logIndexLast - logItemsPerPage;
                                              const visibleRecipients = c.recipients.slice(logIndexFirst, logIndexLast);

                                              return visibleRecipients.map((rec, rIdx) => (
                                                <tr key={rIdx} style={rec.status === 'failed' ? { backgroundColor: '#fff5f5' } : {}}>
                                                  <td className="font-weight-bold text-dark" data-label="Student">{rec.studentName}</td>
                                                  <td data-label="Email / Phone">
                                                    <div style={{ fontSize: '13.5px' }}>{rec.email}</div>
                                                    <small className="text-muted d-block">{rec.phone || 'N/A'}</small>
                                                  </td>
                                                  <td style={{ whiteSpace: 'normal', fontSize: '12px', minWidth: '350px' }} data-label="Custom Message Sent">
                                                    {rec.customMessage}
                                                  </td>
                                                  <td className="text-center action-cell" data-label="Status">
                                                    <Badge 
                                                      color={
                                                        rec.status === 'sent' ? 'success' : 
                                                        rec.status === 'failed' ? 'danger' : 
                                                        rec.status === 'skipped' ? 'info' : 'warning'
                                                      } 
                                                      className="py-1 px-3 text-uppercase"
                                                    >
                                                      {rec.status}
                                                    </Badge>
                                                    {rec.errorMessage && (
                                                      <small className="text-danger d-block mt-1 font-italic">{rec.errorMessage}</small>
                                                    )}
                                                  </td>
                                                </tr>
                                              ));
                                            })()}
                                          </tbody>
                                        </Table>
                                        
                                        {(() => {
                                          const currentLogPage = campaignLogPages[c.campaignId] || 1;
                                          const logTotalPages = Math.ceil(c.recipients.length / logItemsPerPage);
                                          const logIndexLast = currentLogPage * logItemsPerPage;
                                          const logIndexFirst = logIndexLast - logItemsPerPage;

                                          return logTotalPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                                              <small className="text-muted font-weight-bold">
                                                Showing {logIndexFirst + 1} to {Math.min(logIndexLast, c.recipients.length)} of {c.recipients.length} entries
                                              </small>
                                              <div className="d-flex" style={{ gap: '6px' }}>
                                                <Button
                                                  color="secondary"
                                                  size="sm"
                                                  disabled={currentLogPage === 1}
                                                  onClick={() => setCampaignLogPages(prev => ({ ...prev, [c.campaignId]: currentLogPage - 1 }))}
                                                  style={{ borderRadius: '4px', textTransform: 'none' }}
                                                >
                                                  Previous
                                                </Button>
                                                <Badge color="info" className="d-flex align-items-center px-3" style={{ fontSize: '12px' }}>
                                                  Page {currentLogPage} of {logTotalPages}
                                                </Badge>
                                                <Button
                                                  color="secondary"
                                                  size="sm"
                                                  disabled={currentLogPage === logTotalPages}
                                                  onClick={() => setCampaignLogPages(prev => ({ ...prev, [c.campaignId]: currentLogPage + 1 }))}
                                                  style={{ borderRadius: '4px', textTransform: 'none' }}
                                                >
                                                  Next
                                                </Button>
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card>
          </TabPane>

          {/* SAVED CAMPAIGNS TAB */}
          <TabPane tabId="drafts">
            <Row>
              <Col>
                <Card className="shadow border-0 card-premium">
                  <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center pt-3 pb-2 px-3">
                    <div className="d-flex align-items-center">
                      <div className="mr-2 d-flex align-items-center justify-content-center" style={{ width: '26px', height: '26px', borderRadius: '6px', backgroundColor: '#d4edda', color: '#2dce89' }}>
                        <FaBookmark size={11} />
                      </div>
                      <h6 className="mb-0 font-weight-bold text-dark text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.4px' }}>Saved Campaigns</h6>
                    </div>
                    <Badge color="success" className="px-2 py-1" style={{ fontSize: '10px', borderRadius: '6px' }}>{drafts.length} Saved</Badge>
                  </CardHeader>
                  <CardBody className="px-3 pt-0 pb-3">
                    {loadingDrafts ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" style={{ width: '1.5rem', height: '1.5rem' }} />
                        <p className="mt-2 text-muted" style={{ fontSize: '11px' }}>Loading saved campaigns...</p>
                      </div>
                    ) : drafts.length === 0 ? (
                      <div className="text-center py-5">
                        <FaBookmark size={28} className="text-muted mb-2" style={{ opacity: 0.3 }} />
                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>No saved campaigns yet.</p>
                        <p className="text-muted" style={{ fontSize: '11px' }}>Use "Save Campaign" from the Campaign Dispatcher to save your first one.</p>
                      </div>
                    ) : (
                      <Table className="align-items-center table-flush" responsive style={{ fontSize: '12px' }}>
                        <thead className="thead-light">
                          <tr>
                            <th style={{ fontSize: '10px' }}>Name</th>
                            <th style={{ fontSize: '10px' }}>Filters</th>
                            <th style={{ fontSize: '10px' }}>Recipients</th>
                            <th style={{ fontSize: '10px' }}>Template Preview</th>
                            <th style={{ fontSize: '10px' }}>Saved</th>
                            <th style={{ fontSize: '10px' }}>Mode</th>
                            <th className="text-center" style={{ fontSize: '10px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drafts.map(draft => (
                            <tr key={draft.draftId}>
                              <td>
                                <div className="font-weight-bold text-dark" style={{ fontSize: '12px' }}>{draft.name}</div>
                                <small className="text-muted" style={{ fontSize: '10px' }}>{draft.draftId}</small>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap" style={{ gap: '3px' }}>
                                  {(draft.classFilters || []).map(f => (
                                    <Badge key={f} color="light" className="text-dark border" style={{ fontSize: '9px', padding: '2px 6px' }}>{accountLabel(f)}</Badge>
                                  ))}
                                  {(draft.subscriptionFilters || []).map(f => (
                                    <Badge key={f} color={f === 'active' ? 'success' : f === 'expired' ? 'danger' : 'warning'} style={{ fontSize: '9px', padding: '2px 6px' }}>{f}</Badge>
                                  ))}
                                </div>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {draft.recipientCount > 0 ? (
                                  <>
                                    <Badge color="success" style={{ fontSize: '9px', padding: '2px 7px' }}>
                                      {draft.recipientCount} users
                                    </Badge>
                                    <small className="text-muted d-block mt-1" style={{ fontSize: '9px' }}>Saved snapshot</small>
                                  </>
                                ) : (
                                  <>
                                    <Badge color="secondary" style={{ fontSize: '9px', padding: '2px 7px' }}>None saved</Badge>
                                    <small className="text-muted d-block mt-1" style={{ fontSize: '9px' }}>Refresh needed</small>
                                  </>
                                )}
                              </td>
                              <td>
                                <div className="text-muted" style={{ fontSize: '10px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {draft.messageTemplate || <span className="font-italic">No template</span>}
                                </div>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <small className="text-muted" style={{ fontSize: '10px' }}>{moment(draft.updatedAt).fromNow()}</small>
                              </td>
                              <td>
                                <Badge color={draft.simulate ? 'secondary' : 'danger'} style={{ fontSize: '9px', padding: '2px 6px' }}>
                                  {draft.simulate ? 'Sandbox' : 'Live'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center" style={{ gap: '4px' }}>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleLoadDraft(draft)}
                                    title="Load & Edit"
                                    className="font-weight-bold px-2 py-1"
                                    style={{ fontSize: '10px', borderRadius: '5px' }}
                                  >
                                    <FaEdit size={9} className="mr-1" /> Load
                                  </Button>
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => {
                                      handleLoadDraft(draft);
                                      setTimeout(() => setActiveTab('send'), 100);
                                    }}
                                    title="Load and Send"
                                    className="font-weight-bold px-2 py-1"
                                    style={{ fontSize: '10px', borderRadius: '5px' }}
                                  >
                                    <FaPlay size={9} className="mr-1" /> Send
                                  </Button>
                                  <Button
                                    color="danger"
                                    outline
                                    size="sm"
                                    onClick={() => handleDeleteDraft(draft.draftId)}
                                    title="Delete"
                                    className="font-weight-bold px-2 py-1"
                                    style={{ fontSize: '10px', borderRadius: '5px' }}
                                  >
                                    <FaTrashAlt size={9} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </Container>

      {/* Global Confirm / Alert Dialog */}
      <Modal isOpen={confirmDialog.isOpen} toggle={closeDialog} centered>
        <ModalHeader toggle={closeDialog} className={confirmDialog.type === 'confirm' ? 'text-primary' : 'text-warning'}>
          {confirmDialog.title || (confirmDialog.type === 'confirm' ? 'Confirm Action' : 'Alert')}
        </ModalHeader>
        <ModalBody>
          <div style={{ whiteSpace: 'pre-wrap' }}>{confirmDialog.message}</div>
        </ModalBody>
        <ModalFooter>
          {confirmDialog.type === 'confirm' && (
            <Button color="secondary" onClick={confirmDialog.onCancel || closeDialog}>
              {confirmDialog.cancelText}
            </Button>
          )}
          <Button color={confirmDialog.type === 'confirm' ? 'primary' : 'warning'} onClick={confirmDialog.onConfirm || closeDialog}>
            {confirmDialog.confirmText}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default BulkMessage;
